import React from 'react';

import { sizes } from 'Theme/modules/sizes';
import { createContextAwareUrl } from './utils/urlUtils';
import { AnchorProps } from './types';
import { AnchorMapping } from './styled';

import { HelpIcon, LaunchIcon } from '../../icons';

import { isExternalLink } from './isExternalLink';

/**
 * Anchor component for handling context-aware links with consistent functionality and styling options.
 *
 * https://www.figma.com/design/KFfBvJvYT4evKe9eiLEnwD/%F0%9F%8E%A8-Design-System?node-id=1170-63891&m=dev
 *
 * @param variant - The visual style variant ('base' or 'unstyled')
 * @param href - The URL the link points to (will be processed to be context-aware)
 * @param showHelpIcon - Whether to show a help icon on the left (defaults to false)
 * @param children - The content of the anchor
 * @param rest - All other standard anchor HTML attributes
 */
export const Anchor = React.memo(
  ({
    variant = 'base',
    size = 'base',
    href,
    showHelpIcon = false,
    leftIconSlot = null,
    hideExternalLinkIcon = false,
    allowedHostnames = [],
    children,
    target,
    rel,
    ...rest
  }: AnchorProps) => {
    // Process the URL to make it context-aware
    const processedHref = href ? createContextAwareUrl(href) : undefined;

    // Determine if link is external based on processed href
    const isExternal = processedHref ? isExternalLink(processedHref, allowedHostnames) : false;

    // Set security attributes for external links if not given in props
    const secureTarget = target || (isExternal ? '_blank' : undefined);
    const secureRel = rel || (isExternal ? 'noopener noreferrer' : undefined);

    // Lookup the appropriate styled component based on variant
    const AnchorComponent = AnchorMapping[variant];

    // Show icon when when link is external and/or will open new tab (and not overridden by variant or hideExternalLinkIcon)
    const shouldShowExternalLinkIcon =
      (isExternal || secureTarget === '_blank') && !hideExternalLinkIcon && variant === 'base';

    return (
      <AnchorComponent
        href={processedHref}
        target={secureTarget}
        rel={secureRel}
        size={size}
        {...rest}
      >
        {leftIconSlot}
        {showHelpIcon && !leftIconSlot && (
          <HelpIcon role="presentation" data-testid="help-icon" style={{ marginRight: sizes.xs }} />
        )}

        {children}
        {shouldShowExternalLinkIcon && (
          <LaunchIcon
            role="presentation"
            data-testid="external-icon"
            style={{ marginLeft: sizes.xs }}
          />
        )}
      </AnchorComponent>
    );
  },
);
